import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import NProgress from 'nprogress';

/**
 * Interface untuk state loading
 *
 * @interface LoadingState
 * @property {boolean} isLoading - Status loading saat ini
 * @property {string} [loadingMessage] - Pesan yang ditampilkan selama loading
 * @property {number} progress - Persentase progress loading (0-100)
 * @property {number} [startTime] - Timestamp saat loading dimulai (untuk perhitungan durasi)
 */
interface LoadingState {
  isLoading: boolean;
  loadingMessage?: string;
  progress: number;
  startTime?: number;
}

/**
 * Interface untuk aksi-aksi yang dapat dilakukan pada loading store
 *
 * @interface LoadingActions
 * @property {function} startLoading - Memulai loading dengan pesan opsional
 * @property {function} stopLoading - Menghentikan loading
 * @property {function} setProgress - Mengatur persentase progress loading
 * @property {function} resetLoading - Mereset state loading ke nilai awal
 */
interface LoadingActions {
  startLoading: (message?: string) => void;
  stopLoading: () => void;
  setProgress: (progress: number) => void;
  resetLoading: () => void;
}

/**
 * Tipe gabungan untuk store loading yang berisi state dan aksi
 */
type LoadingStore = LoadingState & LoadingActions;

// Configure NProgress globally dengan optimasi
NProgress.configure({
  showSpinner: false,
  trickleSpeed: 200,
  minimum: 0.08,
  easing: 'ease',
  speed: 400,
  trickle: true,
});

// Debounce untuk mencegah multiple rapid calls
let loadingTimeout: NodeJS.Timeout | null = null;
let progressTimeout: NodeJS.Timeout | null = null;

// Konfigurasi delay berdasarkan mode
const LOADING_DELAY = process.env.NODE_ENV === 'production' ? 0 : 100;

export const useLoadingStore = create<LoadingStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      isLoading: false,
      loadingMessage: undefined,
      progress: 0,
      startTime: undefined,

      // Actions
      startLoading: (message?: string) => {
        const currentState = get();

        // Prevent multiple loading states
        if (currentState.isLoading) {
          return;
        }

        // Clear any existing timeouts
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }

        set(
          {
            isLoading: true,
            loadingMessage: message,
            progress: 0,
            startTime: Date.now(),
          },
          false,
          'startLoading'
        );

        // Start NProgress dengan delay untuk mencegah flicker pada navigasi cepat
        // Di production, tidak ada delay untuk memastikan NProgress selalu terlihat
        loadingTimeout = setTimeout(() => {
          NProgress.start();
        }, LOADING_DELAY);
      },

      stopLoading: () => {
        const currentState = get();

        // Clear timeouts
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }
        if (progressTimeout) {
          clearTimeout(progressTimeout);
          progressTimeout = null;
        }

        // Calculate loading duration untuk debugging
        const duration = currentState.startTime
          ? Date.now() - currentState.startTime
          : 0;

        // Track slow navigation silently
        if (duration > 5000) {
          // Could send to analytics service here
        }

        set(
          {
            isLoading: false,
            loadingMessage: undefined,
            progress: 100,
            startTime: undefined,
          },
          false,
          'stopLoading'
        );

        NProgress.done();

        // Smooth scroll to top setelah navigation dengan delay
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 50);
        }
      },

      setProgress: (progress: number) => {
        const clampedProgress = Math.min(Math.max(progress, 0), 100);

        // Debounce progress updates
        if (progressTimeout) {
          clearTimeout(progressTimeout);
        }

        progressTimeout = setTimeout(() => {
          set({ progress: clampedProgress }, false, 'setProgress');
          NProgress.set(clampedProgress / 100);
        }, 50);
      },

      resetLoading: () => {
        // Clear all timeouts
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }
        if (progressTimeout) {
          clearTimeout(progressTimeout);
          progressTimeout = null;
        }

        set(
          {
            isLoading: false,
            loadingMessage: undefined,
            progress: 0,
            startTime: undefined,
          },
          false,
          'resetLoading'
        );

        // Force remove NProgress
        NProgress.remove();
      },
    }),
    {
      name: 'loading-store',
    }
  )
);

/**
 * Selector hooks untuk mengakses state dan aksi loading store
 * Dioptimasi dengan shallow comparison untuk mencegah render ulang yang tidak perlu
 */

/**
 * Hook untuk mengakses status loading
 * @returns {boolean} Status loading saat ini
 */
export const useIsLoading = () => useLoadingStore((state) => state.isLoading);

/**
 * Hook untuk mengakses pesan loading
 * @returns {string | undefined} Pesan loading saat ini
 */
export const useLoadingMessage = () =>
  useLoadingStore((state) => state.loadingMessage);

/**
 * Hook untuk mengakses persentase progress loading
 * @returns {number} Persentase progress (0-100)
 */
export const useProgress = () => useLoadingStore((state) => state.progress);

/**
 * Hook untuk mengakses semua aksi loading
 * Menggunakan selector terpisah untuk setiap aksi untuk memastikan stabilitas referensi
 *
 * @returns {Object} Objek berisi semua aksi loading
 * @returns {Function} startLoading - Memulai loading dengan pesan opsional
 * @returns {Function} stopLoading - Menghentikan loading
 * @returns {Function} setProgress - Mengatur persentase progress loading
 * @returns {Function} resetLoading - Mereset state loading ke nilai awal
 *
 * @example
 * // Dalam komponen React
 * const { startLoading, stopLoading } = useLoadingActions();
 *
 * // Memulai loading dengan pesan
 * const handleClick = () => {
 *   startLoading('Memuat data...');
 *   fetchData().finally(() => stopLoading());
 * };
 */
export const useLoadingActions = () => {
  const startLoading = useLoadingStore((state) => state.startLoading);
  const stopLoading = useLoadingStore((state) => state.stopLoading);
  const setProgress = useLoadingStore((state) => state.setProgress);
  const resetLoading = useLoadingStore((state) => state.resetLoading);

  return {
    startLoading,
    stopLoading,
    setProgress,
    resetLoading,
  };
};
